import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { useInfiniteQuery, useQuery, useQueryClient } from "@tanstack/react-query";
import debounce from "lodash/debounce";
import mixpanel from "mixpanel-browser";
import ContentContainer from "../../../templates/content-container/contentContainer";
import { DataTable } from "../../../ui-components/data-table";
import { PAGE_IDS } from "../../../app/data/common/pageIds";
import { QUERY_KEYS } from "../../../app/data/common/queryKeys";
import { Routes } from "../../../app/route/RoutesConfig";
import { userSelector } from "../../../slices/user/userSlice";
import CommunicationsService from "../../../app/data/communications/communicationsService";
import PickupRequestsService from "../../../app/data/pickup-requests/pickupRequestsService";
import { InboundMessageListItem, InboundProcessingState } from "../../../app/data/communications/models";
import EmailViewerSidebar from "../../common/email-viewer/EmailViewerSidebar";
import PickupDetails from "../../common/pickup-details/pickupDetails";
import { UpdatePickupModel } from "../../../app/data/pickup/models";
import { pickupDetailsSelector } from "../../../slices/pickup/pickupDetailsSlice";
import { getPickup } from "../../../slices/pickup/pickupDetailsSlice";
import {
  getDriverById,
  pickupAssignmentSelector,
} from "../../../slices/pickup/pickupAssignmentSlice";
import { getPickupDetails } from "../../../services/pickups";
import getPickupInboxColumns, {
  PICKUP_INBOX_COLUMN_OPTIONS,
  PICKUP_INBOX_COLUMNS_HIDDEN_BY_DEFAULT,
} from "./inboxColumns";
import { PickupInboxPath, PickupInboxPathParams } from "./route";
import InboxSummaryTiles, { InboxSummaryFilter } from "./components/inboxSummaryTiles";
import InboxFilters, { InboxFiltersState } from "./components/inboxFilters";
import MessageDetailSidebar from "./components/messageDetailSidebar";
import { INBOX_TRANSACTIONAL_QUERY_OPTIONS } from "./utils/pickupInboxQueryOptions";
import "./pickupInbox.scss";

const communicationsService = CommunicationsService.getInstance();
const pickupRequestsService = PickupRequestsService.getInstance();

const PAGE_SIZE = 25;

const PickupInboxPage: React.FC = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const params = useParams() as PickupInboxPathParams;
  const queryClient = useQueryClient();
  const userState = useSelector(userSelector);
  const pickupDetailsState = useSelector(pickupDetailsSelector);
  const pickupAssignmentState = useSelector(pickupAssignmentSelector);

  const messageIdParam = params[PickupInboxPath.messageId];
  const selectedMessageId = messageIdParam ? Number(messageIdParam) : null;

  const [emailViewerMessage, setEmailViewerMessage] = useState<InboundMessageListItem | null>(null);
  const [pickupDetailsNumber, setPickupDetailsNumber] = useState("");
  const [pickupDetailsDriverName, setPickupDetailsDriverName] = useState("");
  const [summaryFilter, setSummaryFilter] = useState<InboxSummaryFilter>(null);

  const [senderInput, setSenderInput] = useState("");
  const [debouncedSender, setDebouncedSender] = useState("");
  const [processingStateFilter, setProcessingStateFilter] = useState<InboundProcessingState | "">("");
  const [hasPendingReviewFilter, setHasPendingReviewFilter] = useState(false);

  const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>(() => {
    const hidden = new Set(PICKUP_INBOX_COLUMNS_HIDDEN_BY_DEFAULT);
    return PICKUP_INBOX_COLUMN_OPTIONS.reduce(
      (acc, { id }) => ({ ...acc, [id]: !hidden.has(id) }),
      {} as Record<string, boolean>
    );
  });
  const [columnOrder, setColumnOrder] = useState<string[]>(() =>
    PICKUP_INBOX_COLUMN_OPTIONS.map(({ id }) => id)
  );

  const expandPickupRequestId = useMemo(() => {
    const q = new URLSearchParams(location.search);
    const id = q.get("pickupRequestId");
    return id ? Number(id) : null;
  }, [location.search]);

  const terminalPrefix = userState.activeTerminal?.id
    ? `/${userState.activeTerminal.id}`
    : "";

  const syncUrl = useCallback(
    (messageId: number | null, pickupRequestId?: number | null) => {
      const q = new URLSearchParams();
      if (hasPendingReviewFilter) q.set("hasPendingReview", "true");
      if (processingStateFilter) q.set("processingState", processingStateFilter);
      if (debouncedSender) q.set("sender", debouncedSender);
      if (pickupRequestId) q.set("pickupRequestId", String(pickupRequestId));

      const path = messageId
        ? `${terminalPrefix}${Routes.pickup.inbox}/${messageId}`
        : `${terminalPrefix}${Routes.pickup.inbox}`;
      const search = q.toString();
      history.replace({ pathname: path, search: search ? `?${search}` : "" });
    },
    [
      terminalPrefix,
      hasPendingReviewFilter,
      processingStateFilter,
      debouncedSender,
      history,
    ]
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSetSender = useCallback(
    debounce((value: string) => setDebouncedSender(value), 500),
    []
  );

  useEffect(() => {
    mixpanel.track("page_viewed", { page: "pickup_inbox" });
  }, []);

  useEffect(() => {
    const q = new URLSearchParams(location.search);
    if (q.get("hasPendingReview") === "true") setHasPendingReviewFilter(true);
    if (q.get("sender")) {
      setSenderInput(q.get("sender") || "");
      setDebouncedSender(q.get("sender") || "");
    }
    if (q.get("processingState")) {
      setProcessingStateFilter(q.get("processingState") as InboundProcessingState);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: [QUERY_KEYS.PICKUP_INBOX_STATS],
    queryFn: () => pickupRequestsService.getStats(),
    retry: false,
    ...INBOX_TRANSACTIONAL_QUERY_OPTIONS,
  });

  const listQueryKey = [
    QUERY_KEYS.PICKUP_INBOX_MESSAGES,
    debouncedSender,
    processingStateFilter,
    hasPendingReviewFilter,
  ];

  const {
    data: messagesData,
    fetchNextPage,
    isFetching,
    isFetchingNextPage,
    isLoading,
    isPreviousData,
  } = useInfiniteQuery({
    queryKey: listQueryKey,
    queryFn: async ({ pageParam }) =>
      communicationsService.getInboundMessages({
        pageSize: PAGE_SIZE,
        next: pageParam as string | undefined,
        sender: debouncedSender || undefined,
        processingState: processingStateFilter || undefined,
        hasPendingReview: hasPendingReviewFilter || undefined,
      }),
    getNextPageParam: (lastPage) => lastPage.pagination?.next ?? undefined,
    keepPreviousData: true,
    retry: false,
  });

  const messages = useMemo(
    () => messagesData?.pages.flatMap((p) => p.messages) ?? [],
    [messagesData]
  );

  const hasNextPage = !!messagesData?.pages[messagesData.pages.length - 1]?.pagination?.next;

  const onViewEmail = useCallback((row: InboundMessageListItem, e: React.MouseEvent) => {
    e.stopPropagation();
    setEmailViewerMessage(row);
  }, []);

  const columns = useMemo(
    () => getPickupInboxColumns({ onViewEmail }),
    [onViewEmail]
  );

  const onRowClick = (row: InboundMessageListItem) => {
    syncUrl(row.id, expandPickupRequestId);
  };

  const onCloseDetail = () => {
    syncUrl(null);
  };

  const onCloseEmailViewer = () => setEmailViewerMessage(null);

  const onPickupNumberClick = (pickupNumber: string) => {
    setPickupDetailsNumber(pickupNumber);
    dispatch(getPickup(pickupNumber));
  };

  const onClosePickupDetails = () => {
    setPickupDetailsNumber("");
  };

  useEffect(() => {
    setPickupDetailsDriverName("");
    const driverId = pickupDetailsState.pickup?.pickup?.driverId;
    if (driverId == null) return;
    dispatch(getDriverById(driverId, (name) => setPickupDetailsDriverName(name), true));
  }, [dispatch, pickupDetailsState.pickup?.pickup?.pickupId, pickupDetailsState.pickup?.pickup?.driverId]);

  const invalidateInboxQueries = useCallback(() => {
    queryClient.invalidateQueries([QUERY_KEYS.PICKUP_INBOX_MESSAGES]);
    queryClient.invalidateQueries([QUERY_KEYS.PICKUP_INBOX_STATS]);
    if (selectedMessageId) {
      queryClient.invalidateQueries([
        QUERY_KEYS.PICKUP_INBOX_MESSAGE_DETAIL,
        selectedMessageId,
      ]);
    }
  }, [queryClient, selectedMessageId]);

  const onUpdatePickupFromDetails = useCallback(
    (_pickup: UpdatePickupModel) => {
      invalidateInboxQueries();
      if (pickupDetailsNumber) {
        dispatch(getPickup(pickupDetailsNumber));
      }
    },
    [dispatch, invalidateInboxQueries, pickupDetailsNumber]
  );

  const onReschedulePickupFromDetails = useCallback(
    (_pickup: UpdatePickupModel) => {
      invalidateInboxQueries();
      if (pickupDetailsNumber) {
        dispatch(getPickup(pickupDetailsNumber));
      }
    },
    [dispatch, invalidateInboxQueries, pickupDetailsNumber]
  );

  const pickupDetails = useMemo(() => {
    if (!pickupDetailsState.pickup) return null;
    const base = getPickupDetails(pickupDetailsState.pickup);
    if (!base) return null;
    const driverName = pickupDetailsDriverName || base.driverName;
    return driverName ? { ...base, driverName } : base;
  }, [pickupDetailsState.pickup, pickupDetailsDriverName]);

  const onSummaryApply = (filter: InboxSummaryFilter) => {
    setSummaryFilter(filter);
    if (filter?.type === "hasPendingReview") {
      setHasPendingReviewFilter(true);
      setProcessingStateFilter("");
    } else if (filter?.type === "processingState") {
      setProcessingStateFilter(filter.value);
      setHasPendingReviewFilter(false);
    }
    syncUrl(selectedMessageId);
  };

  const onClearFilters = () => {
    setSenderInput("");
    setDebouncedSender("");
    setProcessingStateFilter("");
    setHasPendingReviewFilter(false);
    setSummaryFilter(null);
    syncUrl(selectedMessageId);
  };

  const onReviewComplete = () => {
    queryClient.invalidateQueries([QUERY_KEYS.PICKUP_INBOX_MESSAGES]);
    queryClient.invalidateQueries([QUERY_KEYS.PICKUP_INBOX_STATS]);
    if (selectedMessageId) {
      queryClient.invalidateQueries([
        QUERY_KEYS.PICKUP_INBOX_MESSAGE_DETAIL,
        selectedMessageId,
      ]);
    }
  };

  const filtersState: InboxFiltersState = {
    sender: senderInput,
    processingState: processingStateFilter,
    hasPendingReview: hasPendingReviewFilter,
  };

  const selectedListRow = messages.find((m) => m.id === selectedMessageId);

  return (
    <ContentContainer pageId={PAGE_IDS.PICKUP_INBOX} title="Inbox">
      <div className="xgs-pickup-inbox">
        <InboxSummaryTiles
          stats={stats}
          isLoading={statsLoading}
          activeFilter={summaryFilter}
          onApplyFilter={onSummaryApply}
        />

        <InboxFilters
          filters={filtersState}
          onSenderChange={(v) => {
            setSenderInput(v);
            debouncedSetSender(v);
            syncUrl(selectedMessageId);
          }}
          onProcessingStateChange={(v) => {
            setProcessingStateFilter(v);
            setSummaryFilter(null);
            syncUrl(selectedMessageId);
          }}
          onClearFilters={onClearFilters}
        />

        <DataTable
          columns={columns}
          data={messages}
          isLoading={isLoading || (isFetching && isPreviousData)}
          keepTableOnLoading
          cursorPointer
          ignoreCellClick="viewEmail"
          rowHeight={48}
          noResultsText="No emails in inbox"
          infiniteScroll
          infiniteScrollLoading={isFetchingNextPage}
          infiniteScrollHasNext={hasNextPage}
          onInfiniteScroll={() => fetchNextPage()}
          onRowClicked={onRowClick}
          showColumnSelector
          columnSelectorOptions={PICKUP_INBOX_COLUMN_OPTIONS}
          columnVisibility={columnVisibility}
          onColumnVisibilityChange={setColumnVisibility}
          columnOrder={columnOrder}
          onColumnOrderChange={setColumnOrder}
          enableColumnResize
          enableColumnReorder
          stickyHeaders
          responsive
        />
      </div>

      <MessageDetailSidebar
        messageId={selectedMessageId}
        show={selectedMessageId != null}
        onClose={onCloseDetail}
        expandPickupRequestId={expandPickupRequestId}
        onViewEmail={() => {
          if (selectedListRow) setEmailViewerMessage(selectedListRow);
        }}
        onPickupNumberClick={onPickupNumberClick}
        onReviewComplete={onReviewComplete}
      />

      <EmailViewerSidebar
        messageId={emailViewerMessage?.id ?? null}
        show={!!emailViewerMessage}
        onClose={onCloseEmailViewer}
        metadata={
          emailViewerMessage
            ? {
                sender: emailViewerMessage.sender,
                receiver: emailViewerMessage.receiver,
                subject: emailViewerMessage.subject,
                receivedAt: emailViewerMessage.receivedAt,
              }
            : undefined
        }
        webLink={emailViewerMessage?.webLink}
      />

      <PickupDetails
        isOpen={!!pickupDetailsNumber}
        pickup={pickupDetails}
        onClose={onClosePickupDetails}
        onUpdatePickup={onUpdatePickupFromDetails}
        onReschedulePickup={onReschedulePickupFromDetails}
        isDriverLoading={
          pickupAssignmentState.requestStarted &&
          pickupAssignmentState.requestCreator === "GET_DRIVER"
        }
        isLoading={
          pickupDetailsState.requestStarted &&
          pickupDetailsState.requestCreator === "GET_PICKUP"
        }
        error={
          pickupDetailsState.requestCreator === "GET_PICKUP"
            ? pickupDetailsState.requestError
            : ""
        }
      />
    </ContentContainer>
  );
};

export default PickupInboxPage;
